import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useChatChannel from "src/realtime/useChatChannel";
import contactService from "../services/contactService";

const DEFAULT_FILTERS = {search:"",tag:"all",dateRange:null,sortBy:"newest",quickFilter:"all"};
const DEFAULT_PAGINATION = {current_page:1,last_page:1,per_page:10,total:0,from:0,to:0};
const SORTS = {newest:["created_at","desc"],oldest:["created_at","asc"],name_asc:["name","asc"],name_desc:["name","desc"],phone_asc:["phone_number","asc"],phone_desc:["phone_number","desc"],status_asc:["status","asc"],status_desc:["status","desc"]};

function useContacts() {
  const [canManageAllContacts,setCanManageAllContacts] = useState(false);
  const [contacts,setContacts] = useState([]);
  const allContacts = contacts;
  const [loading,setLoading] = useState(false);
  const [saving,setSaving] = useState(false);
  const [error,setError] = useState(null);
  const [saveError,setSaveError] = useState(null);
  const [filters,setFiltersState] = useState(DEFAULT_FILTERS);
  const [pagination,setPagination] = useState(DEFAULT_PAGINATION);
  const requestSequence = useRef(0);
  const setup = useMemo(() => {
    try {
      const storedSetup = localStorage.getItem("whatsapp_setup");
      if (!storedSetup) {
        return {
          workspaceId: null,
          whatsappAccountId: null,
          phoneNumberId: null,
        };
      }

      const parsed = JSON.parse(storedSetup);

      return {
        workspaceId: parsed?.workspaceId || parsed?.workspace_id || null,
        whatsappAccountId:
          parsed?.whatsappAccountId ||
          parsed?.whatsapp_account_id ||
          parsed?.accountId ||
          parsed?.account_id ||
          null,
        phoneNumberId: parsed?.phoneNumberId || parsed?.phone_number_id || null,
      };
    } catch (storageError) {
      console.error("Failed to read WhatsApp setup.", storageError);
      return {
        workspaceId: null,
        whatsappAccountId: null,
        phoneNumberId: null,
      };
    }
  }, []);

  const { workspaceId, whatsappAccountId, phoneNumberId } = setup;

  const params = useMemo(() => {
    const [sort,order] = SORTS[filters.sortBy] || SORTS.newest;
    return {page:pagination.current_page,per_page:pagination.per_page,search:filters.search.trim(),
      sort,order,tag:filters.tag === "all" ? undefined : filters.tag.replaceAll("_"," "),
      quick_filter:filters.quickFilter,created_from:filters.dateRange?.from || undefined,created_to:filters.dateRange?.to || undefined};
  },[filters,pagination.current_page,pagination.per_page]);
  const loadContacts = useCallback(async (silent=false) => {
    const sequence=++requestSequence.current;
    if (!workspaceId || !whatsappAccountId || !phoneNumberId) {setContacts([]);setPagination(DEFAULT_PAGINATION);return;}
    if (!silent) setLoading(true);
    setError(null);
    try {
      const response=await contactService.index(workspaceId,whatsappAccountId,phoneNumberId,params);
      if(sequence!==requestSequence.current)return;
      const data=response?.data || {};const rows=Array.isArray(data.data)?data.data:Array.isArray(data)?data:[];
      setCanManageAllContacts(data.capabilities?.can_manage_all_contacts===true);
      setContacts(rows);
      const meta=data.meta || {current_page:1,last_page:1,per_page:params.per_page,total:rows.length,from:rows.length?1:0,to:rows.length};
      setPagination({current_page:Math.min(meta.current_page,meta.last_page),last_page:meta.last_page,per_page:meta.per_page,total:meta.total,from:meta.from || 0,to:meta.to || 0});
    } catch(e) {
      if(sequence!==requestSequence.current)return;
      setContacts([]);setError(e?.response?.data?.message || e.message || "Unable to load contacts.");
      throw e;
    } finally {if(sequence===requestSequence.current)setLoading(false);}
  },[workspaceId,whatsappAccountId,phoneNumberId,params]);
  useEffect(() => {
    const timer=setTimeout(()=>loadContacts().catch(()=>{}),filters.search ? 250 : 0);
    return ()=>{clearTimeout(timer);requestSequence.current+=1;};
  },[loadContacts,filters.search]);
  useChatChannel(phoneNumberId,{onInvalidate:()=>loadContacts(true).catch(()=>{}),onReconnect:()=>loadContacts(true).catch(()=>{})});
  const setFilters=useCallback(value=>{
    setFiltersState(old=>typeof value==="function"?value(old):{...old,...value});
    setPagination(old=>({...old,current_page:1}));
  },[]);
  const setSearch=useCallback(search=>setFilters({search}),[setFilters]);
  const setTag=useCallback(tag=>setFilters({tag}),[setFilters]);
  const setDateRange=useCallback(dateRange=>setFilters({dateRange}),[setFilters]);
  const setSortBy=useCallback(sortBy=>setFilters({sortBy}),[setFilters]);
  const setQuickFilter=useCallback(quickFilter=>setFilters({quickFilter}),[setFilters]);
  const resetFilters=useCallback(()=>setFilters(DEFAULT_FILTERS),[setFilters]);
  const changePage=useCallback(page=>{if(page>=1 && page<=pagination.last_page)setPagination(old=>({...old,current_page:Number(page)}));},[pagination.last_page]);
  const nextPage=useCallback(()=>changePage(pagination.current_page+1),[changePage,pagination.current_page]);
  const previousPage=useCallback(()=>changePage(pagination.current_page-1),[changePage,pagination.current_page]);
  const setPerPage=useCallback(value=>{const size=Math.max(1,Math.min(100,Number(value)||10));setPagination(old=>({...old,per_page:size,current_page:1}));},[]);

  /* =======================================================
     CREATE CONTACT
  ======================================================= */

  const createContact = useCallback(
    async (payload) => {
      if (!workspaceId || !whatsappAccountId || !phoneNumberId) {
        throw new Error("WhatsApp setup is incomplete.");
      }

      try {
        setSaving(true);
        setSaveError(null);
        const response = await contactService.store(
          workspaceId,
          whatsappAccountId,
          phoneNumberId,
          payload,
        );
        await loadContacts();
        return response;
      } catch (createError) {
        console.error("Failed to create contact.", createError);
        setSaveError(
          createError?.response?.data?.message ||
            createError?.message ||
            "Failed to create contact.",
        );
        throw createError;
      } finally {
        setSaving(false);
      }
    },
    [workspaceId, whatsappAccountId, phoneNumberId, loadContacts],
  );

  const updateContact = useCallback(
    async (contactId, payload) => {
      if (!contactId) {
        throw new Error("Contact ID is required.");
      }

      try {
        setSaving(true);
        setSaveError(null);
        const response = await contactService.update(
          workspaceId,
          whatsappAccountId,
          phoneNumberId,
          contactId,
          payload,
        );
        await loadContacts();
        return response;
      } catch (updateError) {
        console.error("Failed to update contact.", updateError);
        setSaveError(
          updateError?.response?.data?.message ||
            updateError?.message ||
            "Failed to update contact.",
        );
        throw updateError;
      } finally {
        setSaving(false);
      }
    },
    [workspaceId, whatsappAccountId, phoneNumberId, loadContacts],
  );

  const deleteContact = useCallback(
    async (contactId) => {
      if (!contactId) {
        throw new Error("Contact ID is required.");
      }

      try {
        setSaving(true);
        setSaveError(null);
        const response = await contactService.destroy(
          workspaceId,
          whatsappAccountId,
          phoneNumberId,
          contactId,
        );
        await loadContacts();
        return response;
      } catch (updateError) {
        console.error("Failed to delete contact.", updateError);
        setSaveError(
          updateError?.response?.data?.message ||
            updateError?.message ||
            "Failed to delete contact.",
        );
        throw updateError;
      } finally {
        setSaving(false);
      }
    },
    [workspaceId, whatsappAccountId, phoneNumberId, loadContacts],
  );

  const saveContact = useCallback(
    async ({ mode = "create", contact = null, payload }) => {
      if (mode === "update") {
        return updateContact(contact?.id, payload);
      }
      return createContact(payload);
    },
    [createContact, updateContact],
  );

  /* =======================================================
     CLEAR SAVE ERROR
  ======================================================= */

  const clearSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  /* =======================================================
     REFRESH
  ======================================================= */

  const refreshContacts = useCallback(async () => {
    return loadContacts();
  }, [loadContacts]);

  /* =======================================================
     RETURN
  ======================================================= */

  return {
    contacts,
    allContacts,
    canManageAllContacts,
    loading,
    saving,
    error,
    saveError,
    filters,
    setFilters,
    setSearch,
    setTag,
    setDateRange,
    setSortBy,
    setQuickFilter,
    resetFilters,
    pagination,
    currentPage: pagination.current_page,
    lastPage: pagination.last_page,
    total: pagination.total,
    from: pagination.from,
    to: pagination.to,
    perPage: pagination.per_page,
    changePage,
    nextPage,
    previousPage,
    setPerPage,
    loadContacts,
    refreshContacts,
    createContact,
    updateContact,
    deleteContact,
    saveContact,
    clearSaveError,
    workspaceId,
    whatsappAccountId,
    phoneNumberId,
  };
}

export default useContacts;
